import { useRef, useState, useEffect } from 'react';

import type { SummaryFilter, GroupByField } from '~entities/summary';

import { DashboardTable } from '~shared/ui/dashboard-table';
import type {
  DashboardTableRef,
  DashboardTableState,
} from '~shared/ui/dashboard-table';
import { Loader } from '~shared/ui/loader';

import Filter from '@breeze-platform-ui/filter';

import { getColumns, type ColumnId } from '../lib/columns';
import { sortToParams } from '../lib/search-params-converter';
import { colActionOptions, colSizingOptions } from '../lib/table-options';
import {
  useSummaryTableData,
  type SummaryQueryParameters,
} from '../model/summary-table-queries';

type Props = {
  filters: SummaryFilter;
};

const DEFAULT_PER_PAGE = 10;
const DEFAULT_GROUP_BY = 'by-merchant';

const defaultTableState: DashboardTableState<ColumnId> = {
  pagination: { page: 0, rowsPerPage: DEFAULT_PER_PAGE },
  sort: undefined,
};

export const SummaryTable = ({ filters }: Props) => {
  const [queryParams, setQueryParams] = useState<SummaryQueryParameters>();

  const { data, isError, isLoading } = useSummaryTableData(queryParams);

  const tableRef = useRef<DashboardTableRef>(null);

  if (isError) {
    // we need to add default errors notifications
  }

  useEffect(() => {
    setQueryParams((prev) => {
      return {
        groupBy: prev?.groupBy || DEFAULT_GROUP_BY,
        searchParams: {
          sort: prev?.searchParams.sort,
          page: 0,
          perPage: prev?.searchParams.perPage ?? DEFAULT_PER_PAGE,
          filter: filters,
        },
      };
    });
    tableRef.current?.setPage(0);
  }, [filters]);

  const handleGroupByChange = (groupBy: GroupByField) => {
    setQueryParams((prev) => ({
      groupBy,
      searchParams: {
        sort: prev?.searchParams.sort,
        page: 0,
        perPage: prev?.searchParams.perPage ?? DEFAULT_PER_PAGE,
        filter: filters,
      },
    }));
    tableRef.current?.setPage(0);
  };

  const handleTableStateChange = ({
    pagination,
    sort,
  }: DashboardTableState) => {
    setQueryParams((prev) => ({
      groupBy: prev?.groupBy ?? DEFAULT_GROUP_BY,
      searchParams: {
        sort: sort && sortToParams(sort),
        page: pagination?.page ?? 0,
        perPage: pagination?.rowsPerPage ?? DEFAULT_PER_PAGE,
        filter: filters,
      },
    }));
  };

  if (isLoading && !data?.items?.length) {
    // Loader when there is still no table data
    return (
      <div className="relative flex min-h-[226px] flex-col gap-8">
        <Loader />
      </div>
    );
  }

  if (!data?.items?.length) {
    return null;
  }

  return queryParams ? (
    <div className="flex min-h-[226px] flex-col">
      <h3 className="mt-10 mb-5 text-2xl font-bold">Group by</h3>
      <Filter
        name="dataField"
        options={[
          { content: 'By merchant', value: 'by-merchant' },
          { content: 'By store', value: 'by-pos' },
        ]}
        value={queryParams.groupBy}
        onChange={(e) => handleGroupByChange(e.target.value as GroupByField)}
      />

      <div className="relative mt-6 flex grow flex-col">
        {/* Loader when the table already has some data and we change search params  */}
        {isLoading && <Loader />}
        {(data?.items?.length ?? 0) > 0 && (
          <DashboardTable
            screen="dashboard"
            isPaginationEnabled
            data={data?.items}
            totalAmount={data?.total}
            columns={getColumns(queryParams.groupBy)}
            defaultState={defaultTableState}
            colSizingOptions={colSizingOptions}
            colActionOptions={colActionOptions}
            innerRef={tableRef}
            onStateChange={handleTableStateChange}
          />
        )}
      </div>
    </div>
  ) : (
    <></>
  );
};
